import { useCallback, useMemo, useState } from 'react';

import {
  Box,
  Stack,
  Select,
  Tooltip,
  MenuItem,
  IconButton,
  InputLabel,
  FormControl,
  SelectChangeEvent,
  InputAdornment,
} from '@mui/material';

import Iconify from 'src/components/iconify';
import { useTranslate } from 'src/locales';
import { SortOrder } from 'src/services/common.types';
import { SortConfig } from 'src/services/api.types';

// ----------------------------------------------------------------------

export type SortOption = {
  label: string;
  key: string;
};

type TableSorterProps = {
  sortOptions: SortOption[];
  value?: SortConfig;
  onChange: (sortConfig: SortConfig) => void;
  placeholder?: string;
  minWidth?: number;
  sx?: object;
};

// ----------------------------------------------------------------------

export default function TableSorter({
  sortOptions,
  value,
  onChange,
  placeholder,
  minWidth = 180,
  sx
}: TableSorterProps) {

  const { t } = useTranslate();

  const defaultPlaceholder = placeholder || t('components.table_sorter.placeholder');

  const [sortKey, setSortKey] = useState<string>(value?.sort_by || '');
  const [sortDirection, setSortDirection] = useState<SortOrder>(value?.sort_order || SortOrder.ASC);

  const handleKeyChange = useCallback(
    (event: SelectChangeEvent<string>) => {
      const newKey = event.target.value;
      if (newKey) {
        setSortKey(newKey);
        onChange({
          sort_by: newKey,
          sort_order: sortDirection,
        });
      }
    },
    [onChange, sortDirection]
  );

  const handleDirectionToggle = useCallback(() => {
    if (sortKey) {
      setSortDirection((prev) => (prev === SortOrder.ASC ? SortOrder.DESC : SortOrder.ASC));
      onChange({
        sort_by: sortKey,
        sort_order: sortDirection === SortOrder.ASC ? SortOrder.DESC : SortOrder.ASC,
      });
    }
  }, [onChange, sortKey, sortDirection]);

  const handleClear = useCallback(
    (event: React.MouseEvent) => {
      event.stopPropagation();
      setSortKey('');
      setSortDirection(SortOrder.ASC);
      onChange({
        sort_by: '',
        sort_order: SortOrder.ASC,
      });
    },
    [onChange]
  );

  const sortTooltip = useMemo(() => {
    if (!sortKey) {
      return t('components.table_sorter.select_field');
    }

    return sortDirection === SortOrder.ASC
      ? t('components.table_sorter.ascending')
      : t('components.table_sorter.descending');
  }, [sortKey, sortDirection, t]);

  return (
    <Stack direction="row" spacing={0} alignItems="center" {...sx}>
      <FormControl size="small" sx={{ minWidth, flexGrow: 1 }}>
        <InputLabel id="table-sorter-label">{defaultPlaceholder}</InputLabel>
        <Select
          labelId="table-sorter-label"
          id="table-sorter-select"
          value={sortKey}
          label={defaultPlaceholder}
          onChange={handleKeyChange}
        >
          <MenuItem value="">
            <em>{t('components.table_sorter.none')}</em>
          </MenuItem>
          {sortOptions.map((option) => (
            <MenuItem key={option.key} value={option.key}>
              {option.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <Tooltip
        title={sortTooltip}
      >
        <span>
          <IconButton
            size="small"
            onClick={handleDirectionToggle}
            disabled={!sortKey}
            color={sortKey ? 'primary' : 'default'}
            sx={{
              ml: 0.5,
              transition: 'all 0.2s',
              '&:hover': {
                transform: sortKey ? 'scale(1.1)' : 'none',
              },
            }}
          >
            <Iconify
              icon={
                sortDirection === 'asc'
                  ? 'mdi:sort-ascending'
                  : 'mdi:sort-descending'
              }
              width={20}
            />
          </IconButton>
        </span>
      </Tooltip>

      {sortKey && (
        <Tooltip title={t('components.table_sorter.clear_sort')}>
          <IconButton
            size="small"
            onClick={handleClear}
            sx={{
              ml: 0.5,
              color: 'text.secondary',
              transition: 'all 0.2s',
              '&:hover': {
                color: 'error.main',
                transform: 'scale(1.1)',
              },
            }}
          >
            <Iconify icon="eva:close-fill" width={20} />
          </IconButton>
        </Tooltip>
      )}
    </Stack>
  );
}